import React, { useContext, useState } from 'react';

import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import { DeviceContext } from '@vyce/core/src/contexts';
import { AppKeyboardDatePicker, AppTextField } from '@vyce/core/src/components/inputs';
import { AppA } from '@vyce/core/src/components';
import { formatDate } from '@vyce/core/src/utils/dates';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';
import {
  SHARE_CODE_FORMATTED_LENGTH,
  SHARE_CODE_PROBLEM,
  formatShareCode,
  normaliseShareCode,
  rtwErrorMessage,
  validShareCode,
} from '@vyce/core/src/utils/shareCode';

import { MAIN_CONTAINER_ID } from '../../../constants';

const shareCodeLink = 'https://www.gov.uk/prove-right-to-work/get-a-share-code-online';
interface Props {
  open: boolean;
  handleClose: Function;
  triggerRTWCheckRequest: Function;
  shareCode?: string;
  dateOfBirth?: string;
  userId?: string;
}

export const ShareCodeDialog: React.FC<Props> = ({
  open,
  handleClose,
  shareCode,
  dateOfBirth,
  triggerRTWCheckRequest,
  userId,
}) => {
  const { isMobile } = useContext(DeviceContext);
  // Shown as W12 345 678, sent as W12345678.
  const [code, setCode] = useState<string>(formatShareCode(shareCode));
  const [birthday, setBirthday] = useState<string | undefined>(dateOfBirth);
  const [loading, setLoading] = useState<boolean>(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  // Why the check didn't start. The dialog stays open so the code can be fixed.
  const [error, setError] = useState<string | null>(null);

  const verify = async () => {
    if (!validShareCode(code)) {
      setCodeError(SHARE_CODE_PROBLEM);
      return;
    }
    setLoading(true);
    setError(null);
    const payload = {
      date_of_birth: birthday,
      rtw_share_code: normaliseShareCode(code),
    };
    try {
      await triggerRTWCheckRequest({ payload, userId });
      handleClose();
    } catch (e) {
      setError(rtwErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      open={open}
      onClose={() => handleClose()}
    >
      <DialogTitle>Right to Work verification</DialogTitle>

      <DialogContent>
        <Box width={isMobile ? '100%' : 400}>
          <AppTextField
            fullWidth
            margin="normal"
            inputProps={{ maxLength: SHARE_CODE_FORMATTED_LENGTH, autoComplete: 'off', spellCheck: false }}
            value={code}
            onChange={e => {
              setCode(formatShareCode(e.target.value));
              setCodeError(null);
              setError(null);
            }}
            placeholder="W12 345 678"
            error={!!codeError}
            helperText={codeError}
            label="Share Code"
          />

          <Box>
            <Typography display="inline" style={{ fontSize: 13 }}>
              Don't have a share code?
            </Typography>{' '}
            <AppA href={shareCodeLink} content="Click here and follow the instructions to get one." />
          </Box>

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <AppKeyboardDatePicker
              value={birthday}
              onChange={date => {
                setBirthday(formatDate(date));
                setError(null);
              }}
              label="Date of Birth"
              autoOk
              margin="normal"
              format="dd/MM/yyyy"
              fullWidth
            />
          </MuiPickersUtilsProvider>

          {error && (
            <Typography role="alert" color="error" style={{ fontSize: 13 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button size="large" variant="outlined" onClick={() => handleClose()}>
          Close
        </Button>
        <Button
          style={{ width: 120 }}
          size="large"
          onClick={verify}
          disabled={!code || loading}
          color="primary"
          variant="contained"
        >
          <ButtonTitleWithLoading title="Verify" loaderVariant="paper" loading={loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
